/**
 * OnboardingTour — first-run coach-mark overlay for new signups.
 *
 * Why this exists: video walkthrough (Feb 2026) showed a brand-new user
 * landing on the dashboard with zero guidance. They didn't know what
 * CFO Buck was, where to go next, or how to upgrade. This component
 * walks them through the 5 most important surfaces.
 *
 * Behaviour:
 *   - Auto-runs once per user (localStorage `onboarding_tour_done_v1`).
 *   - Listens for the `onboarding-tour-start` window event so the Help
 *     menu can re-launch it on demand.
 *   - Skips entirely on public + auth + portal routes.
 *
 * Implementation note: deliberately CSS-only positioning (no Floating-UI)
 * to keep the bundle small. Each step references an element by
 * `data-tour` attribute and positions a floating card next to it.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { X, ArrowRight, ArrowLeft, Sparkles } from 'lucide-react';
import { cn } from '../lib/utils';

const STORAGE_KEY = 'onboarding_tour_done_v1';

const SKIP_PREFIXES = [
  '/login', '/welcome', '/auth-callback',
  '/terms', '/privacy', '/features', '/portal',
  '/sign/', '/owner-admin', '/setup-wizard',
  '/resources', '/contact', '/security',
  '/landing-classic', '/demo', '/book', '/pay/', '/p/', '/c/',
];

// Each step: where to point + what to say. `target` is matched against
// `[data-tour="X"]` selectors on existing UI. Falls back to a centered
// modal when the target isn't on the current page.
const STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to CountBucks',
    body: 'A 30-second tour so you know what each piece does. Skip anytime.',
    target: null,                  // centered hero step
  },
  {
    id: 'dashboard',
    title: 'Your Business Dashboard',
    body: 'Cash on hand, what needs your attention, P&L — all on one screen. Drag any card to rearrange.',
    target: '[data-testid="dash-customize-toggle"]',
    route: '/business-dashboard',
  },
  {
    id: 'cfo_buck',
    title: 'Ask CFO Buck',
    body: 'Your AI financial advisor. Ask any business question — it sees your books.',
    target: '[data-testid="topbar-cfo-btn"]',
  },
  {
    id: 'search',
    title: 'Search anything',
    body: 'Find a transaction, contact, invoice, or page in seconds. Hit ⌘K from anywhere.',
    target: '[data-testid="global-search-btn"]',
  },
  {
    id: 'help',
    title: 'Need help later?',
    body: 'Click the "?" up here anytime to re-launch this tour or reach support.',
    target: '[data-testid="topbar-help-btn"]',
  },
];

export default function OnboardingTour() {
  const location = useLocation();
  const navigate = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [active, setActive] = useState(false);
  const [blocked, setBlocked] = useState(true);
  const [anchor, setAnchor] = useState(null);

  const path = location.pathname;
  const skip = useMemo(
    () => path === '/' || SKIP_PREFIXES.some((p) => path.startsWith(p)),
    [path]
  );

  // Continuously track whether a higher-priority onboarding overlay is on
  // screen — the "Name your business" screen or the SetupWizard dialog, both
  // marked with [data-tour-block]. These must finish BEFORE the tour so the
  // user sees one thing at a time (never a stacked business-name modal +
  // tour + chat bubble). A MutationObserver keeps this in sync as those
  // overlays mount/unmount, which a one-shot timer could not.
  useEffect(() => {
    const check = () => setBlocked(!!document.querySelector('[data-tour-block]'));
    check();
    const obs = new MutationObserver(check);
    obs.observe(document.body, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, []);

  // Auto-start once per user — only once nothing is blocking. Waits for a
  // short "settle" window so we don't start in the brief gap before the
  // workspace fetch resolves and the business-name screen paints.
  useEffect(() => {
    if (skip || blocked) return;
    const seen = (() => { try { return window.localStorage.getItem(STORAGE_KEY); } catch { return null; } })();
    if (seen) return;
    const t = setTimeout(() => {
      if (!document.querySelector('[data-tour-block]')) setActive(true);
    }, 700);
    return () => clearTimeout(t);
  }, [skip, blocked]);

  useEffect(() => {
    const start = () => { setStepIdx(0); setActive(true); };
    window.addEventListener('onboarding-tour-start', start);
    return () => window.removeEventListener('onboarding-tour-start', start);
  }, []);

  // Resolve the anchor element for the current step. Re-run on each
  // step change and on resize so the popover follows the target.
  useEffect(() => {
    if (!active) return;
    const step = STEPS[stepIdx];
    if (!step?.target) { setAnchor(null); return; }
    const tick = () => {
      const el = document.querySelector(step.target);
      if (el) {
        const r = el.getBoundingClientRect();
        setAnchor({
          top: r.bottom + 8,
          left: Math.max(12, Math.min(window.innerWidth - 340, r.left + r.width / 2 - 160)),
          targetRect: r,
        });
      } else {
        setAnchor(null);
      }
    };
    tick();
    const t = setTimeout(tick, 200); // wait for layout transitions
    window.addEventListener('resize', tick);
    window.addEventListener('scroll', tick, true);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', tick);
      window.removeEventListener('scroll', tick, true);
    };
  }, [active, stepIdx, path]);

  const finish = useCallback(() => {
    try { window.localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* ignore */ }
    setActive(false);
    setStepIdx(0);
  }, []);

  const next = useCallback(() => {
    const nextStep = STEPS[stepIdx + 1];
    if (!nextStep) { finish(); return; }
    if (nextStep.route && path !== nextStep.route) {
      navigate(nextStep.route);
    }
    setStepIdx((i) => i + 1);
  }, [stepIdx, path, navigate, finish]);

  const back = useCallback(() => setStepIdx((i) => Math.max(0, i - 1)), []);

  if (!active || skip || blocked) return null;

  const step = STEPS[stepIdx];
  const isLast = stepIdx === STEPS.length - 1;

  // Floating card — anchored next to the target, or centered when no target.
  const cardStyle = anchor
    ? { position: 'fixed', top: anchor.top, left: anchor.left, width: 320, zIndex: 1000 }
    : { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 360, zIndex: 1000 };

  return (
    <>
      {/* Soft scrim — dimmer than a modal so the user still sees the app */}
      <div
        className="fixed inset-0 bg-black/45 backdrop-blur-[1px] z-[999] pointer-events-auto"
        data-testid="onboarding-tour-scrim"
        onClick={(e) => { e.stopPropagation(); }}
      />

      {/* Highlight ring around the target */}
      {anchor?.targetRect && (
        <div
          aria-hidden
          className="fixed border-2 border-emerald-400 rounded-lg pointer-events-none z-[1000] shadow-[0_0_0_4px_rgba(16,185,129,0.25)] animate-pulse"
          style={{
            top: anchor.targetRect.top - 4,
            left: anchor.targetRect.left - 4,
            width: anchor.targetRect.width + 8,
            height: anchor.targetRect.height + 8,
          }}
        />
      )}

      {/* Popover card */}
      <div
        style={cardStyle}
        className={cn(
          'bg-card border border-border rounded-xl shadow-2xl p-4',
          'animate-in fade-in zoom-in-95 duration-200'
        )}
        data-testid={`onboarding-tour-step-${step.id}`}
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-emerald-500" />
            <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
              Step {stepIdx + 1} of {STEPS.length}
            </span>
          </div>
          <button
            type="button"
            onClick={finish}
            aria-label="Skip tour"
            data-testid="onboarding-tour-skip"
            className="text-muted-foreground hover:text-foreground -mr-1 -mt-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <h3 className="text-base font-semibold leading-tight">{step.title}</h3>
        <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{step.body}</p>

        <div className="flex items-center justify-between mt-4 gap-2">
          {stepIdx > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={back}
              data-testid="onboarding-tour-back"
            >
              <ArrowLeft className="w-3.5 h-3.5 mr-1" />
              Back
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={finish}
              data-testid="onboarding-tour-skip-text"
              className="text-muted-foreground"
            >
              Skip
            </Button>
          )}
          <Button
            size="sm"
            onClick={next}
            data-testid="onboarding-tour-next"
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
          >
            {isLast ? 'Got it' : 'Next'}
            {!isLast && <ArrowRight className="w-3.5 h-3.5 ml-1" />}
          </Button>
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-1 mt-3">
          {STEPS.map((s, i) => (
            <span
              key={s.id}
              className={cn(
                'h-1 rounded-full transition-all',
                i === stepIdx ? 'w-6 bg-emerald-500' : i < stepIdx ? 'w-1.5 bg-emerald-500/60' : 'w-1.5 bg-border'
              )}
            />
          ))}
        </div>
      </div>
    </>
  );
}
